import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Heart, Star, Shield, Truck, RotateCcw } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function ProductDetail() {
  const { id } = useParams();
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const product = state.products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Product Not Found</h2>
          <Link to="/products" className="text-red-400 hover:text-red-300">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      dispatch({ type: 'ADD_TO_CART', payload: product });
    }
  };

  const handleBuyNow = () => {
    for (let i = 0; i < quantity; i++) {
      dispatch({ type: 'ADD_TO_CART', payload: product });
    }
    navigate('/checkout');
  };

  // Mock additional images for the gallery
  const productImages = [
    product.image,
    'https://images.pexels.com/photos/1667071/pexels-photo-1667071.jpeg',
    'https://images.pexels.com/photos/1667070/pexels-photo-1667070.jpeg',
  ];

  return (
    <div className="min-h-screen bg-black py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/products" className="inline-flex items-center space-x-2 text-red-400 hover:text-red-300 mb-6 sm:mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm sm:text-base">Back to Products</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-3xl overflow-hidden bg-gray-900">
              <img
                src={productImages[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex space-x-2 sm:space-x-4 overflow-x-auto">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 flex-shrink-0 ${
                    selectedImage === index ? 'border-red-500' : 'border-gray-700'
                  }`}
                >
                  <img src={image} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        i < Math.floor(product.rating)
                          ? 'text-yellow-500 fill-current'
                          : 'text-gray-600'
                      }`}
                    />
                  ))}
                  <span className="text-yellow-500 font-semibold ml-2 text-sm sm:text-base">{product.rating}</span>
                </div>
                <span className="text-gray-400 text-sm sm:text-base">({product.reviews} reviews)</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {product.originalPrice && (
                <span className="text-lg sm:text-xl text-gray-500 line-through">
                  ₹{product.originalPrice.toLocaleString()}
                </span>
              )}
              <span className="text-2xl sm:text-3xl font-bold text-green-400">
                ₹{product.price.toLocaleString()}
              </span>
              {product.discount && (
                <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs sm:text-sm font-bold">
                  {product.discount}% OFF
                </span>
              )}
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Description</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{product.description}</p>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Fragrance Notes</h3>
              <p className="text-gray-300 text-sm sm:text-base">{product.scent}</p>
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-white font-semibold text-sm sm:text-base">Quantity:</label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="bg-gray-800 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-700"
                >
                  -
                </button>
                <span className="text-white font-semibold w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="bg-gray-800 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-700"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-full flex items-center justify-center space-x-2 transition-colors font-semibold"
              >
                <ShoppingCart className="w-5 h-5" />
              </button>
              
              <button
                onClick={handleBuyNow}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-full font-semibold transition-all transform hover:scale-105"
              >
                Buy Now
              </button>

              <button className="bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full transition-colors">
                <Heart className="w-5 h-5" />
              </button>
            </div>

            <div className={`text-center p-3 rounded-full ${product.inStock ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
              {product.inStock ? '✓ In Stock - Ready to Ship' : '✗ Currently Out of Stock'}
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-700">
              <div className="text-center">
                <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                <p className="text-xs sm:text-sm text-gray-300">Free Shipping</p>
              </div>
              <div className="text-center">
                <RotateCcw className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                <p className="text-xs sm:text-sm text-gray-300">7-Day Returns</p>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                <p className="text-xs sm:text-sm text-gray-300">Authentic Products</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-12 sm:mt-16">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">You Might Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {state.products.filter(p => p.id !== product.id).slice(0, 4).map((relatedProduct) => (
              <div key={relatedProduct.id} className="bg-gray-900 rounded-3xl p-4 border border-gray-800">
                <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-32 sm:h-40 object-cover rounded-2xl mb-4" />
                <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">{relatedProduct.name}</h3>
                <p className="text-green-400 font-bold text-sm sm:text-base">₹{relatedProduct.price.toLocaleString()}</p>
                <Link to={`/product/${relatedProduct.id}`} className="block mt-3 text-center bg-red-600 text-white py-2 rounded-full text-xs sm:text-sm font-semibold hover:bg-red-700 transition-colors">
                  View Details
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}